import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CForm, CFormLabel, CFormInput,
  CFormTextarea, CButton, CRow, CCol, CAlert, CSpinner, CFormCheck,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
  cilUser, cilPhone, cilCreditCard, cilBuilding,
  cilEnvelopeClosed, cilHome, cilLocationPin, cilNotes,
  cilArrowLeft, cilSave, cilTask,
} from '@coreui/icons';
import api from '../services/api';

const EMPTY = {
  nama: '', kp: '', sykt: '', telhp: '', telpej: '', email: '',
  alamat1: '', alamat2: '', poskod: '', daerah: '', negeri: '', catatan: '',
  marketing_unsubscribe: false,
};

const NEGERI_LIST = [
  '', 'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan',
  'Pahang', 'Perak', 'Perlis', 'Pulau Pinang', 'Sabah', 'Sarawak',
  'Selangor', 'Terengganu', 'W.P. Kuala Lumpur', 'W.P. Labuan', 'W.P. Putrajaya',
];

function FieldLabel({ icon, children }) {
  return (
    <CFormLabel className="d-flex align-items-center gap-2 mb-1">
      <CIcon icon={icon} size="sm" className="text-success" />{children}
    </CFormLabel>
  );
}

export default function CustomerFormPage() {
  const { uid } = useParams();
  const isEdit = !!uid;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/customers/${uid}`)
      .then(r => {
        const c = r.data.customer;
        setForm({
          nama: c.nama || '', kp: c.kp || '', sykt: c.sykt || '',
          telhp: c.telhp || '', telpej: c.telpej || '', email: c.email || '',
          alamat1: c.alamat1 || '', alamat2: c.alamat2 || '', poskod: c.poskod || '',
          daerah: c.daerah || '', negeri: c.negeri || '', catatan: c.catatan || '',
          marketing_unsubscribe: Number(c.marketing_unsubscribe) === 1,
        });
      })
      .catch(() => setError('Failed to load customer.'))
      .finally(() => setLoading(false));
  }, [uid, isEdit]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: checked }));
      return;
    }
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      if (isEdit) {
        const payload = { ...form, marketing_unsubscribe: form.marketing_unsubscribe ? 1 : 0 };
        await api.put(`/customers/${uid}`, payload);
        setSuccess('Customer updated successfully.');
      } else {
        await api.post('/customers', form);
        setSuccess('Customer registered successfully.');
        setForm(EMPTY);
      }
    } catch (err) {
      const msgs = err.response?.data?.errors?.map(e => e.msg).join(', ');
      setError(msgs || err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-center py-5"><CSpinner color="success" /></div>;

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">{isEdit ? 'Edit Customer' : 'Add Customer'}</h5></CCol>
        <CCol xs="auto" className="d-flex gap-2">
          {isEdit && (
            <CButton color="success" variant="outline" size="sm" onClick={() => navigate(`/jobs?custid=${encodeURIComponent(uid)}&label=${encodeURIComponent('Customer jobs')}`)}>
              <CIcon icon={cilTask} className="me-1" /> Jobs
            </CButton>
          )}
          <CButton color="secondary" variant="outline" size="sm" onClick={() => navigate('/customers')}>
            <CIcon icon={cilArrowLeft} className="me-1" /> Back
          </CButton>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {success && <CAlert color="success">{success}</CAlert>}

      <CForm onSubmit={handleSubmit}>
        <CCard className="mb-3">
          <CCardHeader>
            <CIcon icon={cilUser} className="me-2 text-success" />Personal Info
          </CCardHeader>
          <CCardBody>
            <CRow className="mb-3">
              <CCol md={6}>
                <FieldLabel icon={cilUser}>Full Name <span className="text-danger">*</span></FieldLabel>
                <CFormInput name="nama" value={form.nama} onChange={handleChange} required maxLength={50} placeholder="e.g. Ahmad bin Ali" />
              </CCol>
              <CCol md={6}>
                <FieldLabel icon={cilCreditCard}>IC No.</FieldLabel>
                <CFormInput name="kp" value={form.kp} onChange={handleChange} maxLength={20} placeholder="e.g. 900101-01-1234" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md={6}>
                <FieldLabel icon={cilBuilding}>Company</FieldLabel>
                <CFormInput name="sykt" value={form.sykt} onChange={handleChange} maxLength={50} placeholder="Company name (optional)" />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard className="mb-3">
          <CCardHeader>
            <CIcon icon={cilPhone} className="me-2 text-success" />Contact
          </CCardHeader>
          <CCardBody>
            <CRow className="mb-3">
              <CCol md={6}>
                <FieldLabel icon={cilPhone}>Phone (HP) <span className="text-danger">*</span></FieldLabel>
                <CFormInput name="telhp" value={form.telhp} onChange={handleChange} required maxLength={25} placeholder="e.g. 0123456789" />
              </CCol>
              <CCol md={6}>
                <FieldLabel icon={cilPhone}>Office Phone</FieldLabel>
                <CFormInput name="telpej" value={form.telpej} onChange={handleChange} maxLength={25} placeholder="e.g. 0312345678" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md={6}>
                <FieldLabel icon={cilEnvelopeClosed}>Email</FieldLabel>
                <CFormInput type="email" name="email" value={form.email} onChange={handleChange} maxLength={50} placeholder="e.g. ahmad@email.com" />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard className="mb-3">
          <CCardHeader>
            <CIcon icon={cilHome} className="me-2 text-success" />Address
          </CCardHeader>
          <CCardBody>
            <CRow className="mb-3">
              <CCol md={6}>
                <FieldLabel icon={cilHome}>Address 1</FieldLabel>
                <CFormInput name="alamat1" value={form.alamat1} onChange={handleChange} maxLength={80} placeholder="Street / Unit no." />
              </CCol>
              <CCol md={6}>
                <FieldLabel icon={cilHome}>Address 2</FieldLabel>
                <CFormInput name="alamat2" value={form.alamat2} onChange={handleChange} maxLength={80} placeholder="Area / Taman" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md={3}>
                <FieldLabel icon={cilLocationPin}>Postcode</FieldLabel>
                <CFormInput name="poskod" value={form.poskod} onChange={handleChange} maxLength={25} placeholder="e.g. 50000" />
              </CCol>
              <CCol md={4}>
                <FieldLabel icon={cilLocationPin}>District</FieldLabel>
                <CFormInput name="daerah" value={form.daerah} onChange={handleChange} maxLength={25} placeholder="e.g. Petaling Jaya" />
              </CCol>
              <CCol md={5}>
                <FieldLabel icon={cilLocationPin}>State</FieldLabel>
                <select name="negeri" value={form.negeri} onChange={handleChange} className="form-select">
                  {NEGERI_LIST.map(n => <option key={n} value={n}>{n || '— Select State —'}</option>)}
                </select>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard className="mb-4">
          <CCardHeader>
            <CIcon icon={cilNotes} className="me-2 text-success" />Notes
          </CCardHeader>
          <CCardBody>
            <CFormTextarea name="catatan" value={form.catatan} onChange={handleChange} rows={3} placeholder="Additional notes..." />
          </CCardBody>
        </CCard>

        {isEdit && (
          <CCard className="mb-4">
            <CCardHeader>
              <CIcon icon={cilNotes} className="me-2 text-success" />Marketing
            </CCardHeader>
            <CCardBody>
              <CFormCheck
                id="marketing_unsubscribe"
                name="marketing_unsubscribe"
                label="Unsubscribe from marketing WhatsApp list"
                checked={!!form.marketing_unsubscribe}
                onChange={handleChange}
              />
              <div className="form-text small text-muted mt-1">
                When checked, this customer will not appear in the marketing send queue.
              </div>
            </CCardBody>
          </CCard>
        )}

        <div className="d-flex gap-2">
          <CButton type="submit" color="success" disabled={saving}>
            <CIcon icon={cilSave} className="me-2" />
            {saving ? 'Saving…' : isEdit ? 'Update Customer' : 'Register Customer'}
          </CButton>
          <CButton color="secondary" variant="outline" onClick={() => navigate('/customers')}>Cancel</CButton>
        </div>
      </CForm>
    </>
  );
}
